import { deepMerge, toNumber } from '@sensenet/client-utils';
import { useRepository } from '@sensenet/hooks-react';
import React, { createContext, useMemo } from 'react';
import { v1 } from 'uuid';

/**
 * Adds a globally unique ID to the shape
 */
const addGuidToShape = shape => {
  shape.guid = v1();
  return shape;
};
/**
 * Concatenates avatar url with host
 * @param {DocumentData} documentData
 */


function changeCreatedByUrlToCurrent(documentData) {
  return comment => {
    return { ...comment,
      createdBy: { ...comment.createdBy,
        avatarUrl: `${documentData.hostName}${comment.createdBy.avatarUrl}`
      }
    };
  };
}

export const DocumentViewerApiSettingsContext = /*#__PURE__*/createContext({
  canEditDocument: async () => false,
  canHideRedaction: async () => false,
  canHideWatermark: async () => false,
  commentActions: {
    addPreviewComment: async _ref => {
      let {
        comment
      } = _ref;
      return { ...comment
      };
    },
    deletePreviewComment: async () => ({
      modified: false
    }),
    getPreviewComments: async () => []
  },
  getDocumentData: async () => ({
    documentName: 'example',
    documentType: '',
    fileSizekB: 3,
    hostName: '',
    idOrPath: 1,
    pageNum: 1,
    pageCount: 0,
    shapes: {
      annotations: [],
      highlights: [],
      redactions: []
    }
  }),
  getExistingPreviewImages: async () => [],
  isPreviewAvailable: async () => ({
    Height: 0,
    Width: 0,
    Index: 0
  }),
  regeneratePreviews: async () => ({
    PageCount: -1,
    PreviewCount: 0
  }),
  saveChanges: async () => undefined
});
export const createDefaultApiSettings = repo => ({
  regeneratePreviews: async _ref2 => {
    let {
      document,
      abortController
    } = _ref2;
    return await repo.preview.regenerate({
      idOrPath: document.idOrPath,
      abortController
    });
  },
  saveChanges: async _ref3 => {
    let {
      document,
      abortController
    } = _ref3;
    const reqBody = {
      Shapes: JSON.stringify([{
        redactions: document.shapes.redactions
      }, {
        highlights: document.shapes.highlights
      }, {
        annotations: document.shapes.annotations
      }])
    };
    await repo.patch({
      idOrPath: document.idOrPath,
      content: reqBody,
      requestInit: {
        signal: abortController.signal
      }
    });
  },
  getDocumentData: async settings => {
    const documentData = (await repo.load({
      idOrPath: settings.idOrPath,
      oDataOptions: {
        select: 'all'
      }
    })).d;
    return {
      idOrPath: settings.idOrPath,
      hostName: repo.configuration.repositoryUrl,
      fileSizekB: documentData.Size,
      pageCount: toNumber(documentData.PageCount, -1),
      documentName: documentData.DisplayName || '',
      documentType: documentData.Type || 'File',
      shapes: documentData.Shapes && {
        redactions: JSON.parse(documentData.Shapes)[0].redactions.map(a => addGuidToShape(a)) || [],
        annotations: JSON.parse(documentData.Shapes)[2].annotations.map(a => addGuidToShape(a)) || [],
        highlights: JSON.parse(documentData.Shapes)[1].highlights.map(a => addGuidToShape(a)) || []
      } || {
        redactions: [],
        annotations: [],
        highlights: []
      }
    };
  },
  isPreviewAvailable: async _ref4 => {
    let {
      document,
      version,
      page,
      abortController
    } = _ref4;
    return await repo.preview.available({
      document,
      version,
      page,
      abortController
    });
  },
  canEditDocument: async _ref5 => {
    let {
      idOrPath,
      abortController
    } = _ref5;
    const response = await repo.security.hasPermission(idOrPath, ['Save'], undefined, {
      signal: abortController.signal,
      credentials: 'include'
    });
    return response;
  },
  canHideRedaction: async _ref6 => {
    let {
      idOrPath,
      abortController
    } = _ref6;
    return await repo.security.hasPermission(idOrPath, ['PreviewWithoutRedaction'], undefined, {
      signal: abortController.signal,
      credentials: 'include'
    });
  },
  canHideWatermark: async _ref7 => {
    let {
      idOrPath,
      abortController
    } = _ref7;
    return await repo.security.hasPermission(idOrPath, ['PreviewWithoutWatermark'], undefined, {
      signal: abortController.signal,
      credentials: 'include'
    });
  },
  getExistingPreviewImages: async _ref8 => {
    let {
      document,
      version,
      abortController
    } = _ref8;
    return await repo.preview.getExistingImages({
      document,
      version,
      abortController
    });
  },
  commentActions: {
    addPreviewComment: async _ref9 => {
      let {
        document,
        comment,
        abortController
      } = _ref9;
      const response = await repo.preview.addComment({
        idOrPath: document.idOrPath,
        comment,
        abortController
      });
      return [response].map(changeCreatedByUrlToCurrent(document))[0];
    },
    deletePreviewComment: async _ref10 => {
      let {
        document,
        commentId,
        abortController
      } = _ref10;
      return await repo.preview.deleteComment({
        idOrPath: document.idOrPath,
        commentId,
        abortController
      });
    },
    getPreviewComments: async _ref11 => {
      let {
        document,
        page,
        abortController
      } = _ref11;
      const response = await repo.preview.getComments({
        idOrPath: document.idOrPath,
        page,
        abortController
      });
      return response.map(changeCreatedByUrlToCurrent(document));
    }
  }
});
export const DocumentViewerApiSettingsProvider = _ref12 => {
  let {
    children,
    options
  } = _ref12;
  const repo = useRepository();
  const settings = useMemo(() => deepMerge(createDefaultApiSettings(repo), options), [repo, options]);
  return /*#__PURE__*/React.createElement(DocumentViewerApiSettingsContext.Provider, {
    value: settings
  }, children);
};
//# sourceMappingURL=api-settings.js.map